import React from 'react'
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom'
import PageAbsences from '../../pages/PageAbsences'
import PageAbsencesCreate from '../../pages/PageAbsencesCreate'
import PageAdditionalPayments from '../../pages/PageAdditionalPayments'
import PageAdditionalPaymentsCreate from '../../pages/PageAdditionalPaymentsCreate'
import PageContinuumFormDetail from '../../pages/PageContinuumFormDetail'
import PageContinuumFormIndex from '../../pages/PageContinuumFormIndex'
import PageControlledDocumentDetail from '../../pages/PageControlledDocumentDetail'
import PageControlledDocumentIndex from '../../pages/PageControlledDocumentIndex'
import PageDocs from '../../pages/PageDocs'
import PageError from '../../pages/PageError'
import PageHR from '../../pages/PageHR'
import PageIncidentCreate from '../../pages/PageIncidentCreate'
import PageIndex from '../../pages/PageIndex'
import PageLMS from '../../pages/PageLMS'
import PagePayslipDetail from '../../pages/PagePayslipDetail'
import PagePayslipsIndex from '../../pages/PagePayslipsIndex'
import PageServiceOrderDetail from '../../pages/PageServiceOrderDetail'
import PageServiceOrders from '../../pages/PageServiceOrders'
import PageTimelog from '../../pages/PageTimelog'
import PageTrainingRecordDetail from '../../pages/PageTrainingRecordDetail'
import PageTrainingRecordIndex from '../../pages/PageTrainingRecordIndex'
import PageWork from '../../pages/PageWork'
import { consumeTenantedReturnIfDeepLinkPending, savePostAuthReturnFromWindow } from './authReturnPath'
import AppAuthTokenProvider, { useTokenContext } from './AppTokenProvider'
import { useTenantSessionContext } from './AppDeploymentProvider'
import { getTenantPathPrefix } from './pathTenant'
import { useWindowPathname } from './useWindowPathname'

import type { Router } from '@remix-run/router'

export interface eqxAppConfigRoutes {
    routes: eqxAppConfigRoute[]
    loginPath: string
    disableAuth?: boolean
}
export interface eqxAppConfigRoute {
    path: string
    component?: any
    exact?: boolean
    children?: eqxAppConfigRoute[]
    element?: React.ReactNode
    errorElement?: React.ReactNode
}
interface AppRouterContext {
    router: Router
    navigateToLogin: () => void
    navigate: (path: string) => void
}

const RouteController = () => <Outlet />

const PostAuthTenantedDeepLinkRestore = () => {
    const token = useTokenContext()
    const session = useTenantSessionContext()

    React.useEffect(() => {
        if (!token || !session.organisationURLPrefix) return
        const next = consumeTenantedReturnIfDeepLinkPending(session.organisationURLPrefix, true)
        if (next) window.location.replace(next)
    }, [token, session.organisationURLPrefix])

    return null
}

const RouteControllerRequireAuth = () => (
    <AppAuthTokenProvider>
        <PostAuthTenantedDeepLinkRestore />
        <Outlet />
    </AppAuthTokenProvider>
)

const routerContext = React.createContext<AppRouterContext | undefined>(undefined)
const RouterContextProvider = routerContext.Provider

const authenticatedChildRoutes = [
    { index: true, element: <PageIndex /> },
    {
        path: 'controlled-documents',
        element: <PageControlledDocumentIndex />,
    },
    {
        path: 'controlled-documents/:id',
        element: <PageControlledDocumentDetail />,
    },
    {
        path: 'forms',
        element: <PageContinuumFormIndex />,
    },
    {
        path: 'forms/:id',
        element: <PageContinuumFormDetail />,
    },
    {
        path: 'work',
        element: <PageWork />,
    },
    {
        path: 'incidents/create',
        element: <PageIncidentCreate />,
    },
    {
        path: 'hr',
        element: <PageHR />,
    },
    {
        path: 'docs',
        element: <PageDocs />,
    },
    {
        path: 'training-records',
        element: <PageTrainingRecordIndex />,
    },
    {
        path: 'training-records/:id',
        element: <PageTrainingRecordDetail />,
    },
    {
        path: 'lms',
        element: <PageLMS />,
    },
    {
        path: 'timelog',
        element: <PageTimelog />,
    },
    {
        path: 'service-orders',
        element: <PageServiceOrders />,
    },
    {
        path: 'service-orders/:id',
        element: <PageServiceOrderDetail />,
    },
    {
        path: 'absences/create',
        element: <PageAbsencesCreate />,
    },
    {
        path: 'absences',
        element: <PageAbsences />,
    },
    {
        path: 'payslips',
        element: <PagePayslipsIndex />,
    },
    {
        path: 'payslips/:id',
        element: <PagePayslipDetail />,
    },
    {
        path: 'expenses',
        element: <PageAdditionalPayments />,
    },
    {
        path: 'expenses/create',
        element: <PageAdditionalPaymentsCreate />,
    },
]

const routeTree = [
    {
        element: <RouteController />,
        errorElement: <PageError error="Page not found" />,
        children: [
            {
                path: '',
                element: <RouteControllerRequireAuth />,
                children: authenticatedChildRoutes,
            },
        ],
    },
]

const AppRouter = () => {
    const session = useTenantSessionContext()
    const pathname = useWindowPathname()
    const basename = session.organisationURLPrefix ?? getTenantPathPrefix(pathname)

    if (!basename) {
        throw new Error('AppRouter mounted without an organisation URL segment (expected /_…)')
    }

    const router = React.useMemo(
        () => createBrowserRouter(routeTree as any, { basename }),
        [basename]
    )

    const AppRouterValue = React.useMemo(
        () => ({
            router,
            navigate: (url: string) => {
                router.navigate(url)
            },
            navigateToLogin: () => {
                savePostAuthReturnFromWindow()
                const tenantBase = session.organisationURLPrefix ?? getTenantPathPrefix(pathname)
                if (tenantBase) {
                    const base = tenantBase.replace(/\/$/, '')
                    window.location.assign(`${base}/`)
                    return
                }
                window.location.assign('/')
            },
        }),
        [router, session.organisationURLPrefix, pathname]
    )

    return (
        <RouterContextProvider value={AppRouterValue}>
            <RouterProvider key={basename} router={router} />
        </RouterContextProvider>
    )
}

const useRouterContext = () => {
    const context = React.useContext(routerContext)
    if (context === undefined) {
        throw new Error('useRouterContext must be used within an AppRouter context provider')
    }
    return context
}

export { AppRouter as default, routerContext, useRouterContext, type AppRouterContext }
